import * as React from "react";
import * as XLSX from "xlsx";
import { Button, Modal } from "../components/Modal";
import { Group, ImportGroupMembers, ImportGroupMembersResponse } from "./GroupStore";

interface Props {
    isOpen: boolean;
    organizationId: string;
    group: Group;
    onClose: () => void;
    onImported: (addedCount: number) => void;
    remove?: boolean;
}

export const ImportGroupMembersModal = ({ isOpen, organizationId, group, onClose, onImported, remove = false }: Props) => {
    const [members, setMembers] = React.useState<string[]>([]);
    const [preview, setPreview] = React.useState<ImportGroupMembersResponse | null>(null);
    const [error, setError] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    const [fileName, setFileName] = React.useState("");
    const [identifiersText, setIdentifiersText] = React.useState("");
    const [delegated, setDelegated] = React.useState(false);
    const action = remove ? "Remove" : "Import";

    React.useEffect(() => {
        if (isOpen) {
            setMembers([]);
            setPreview(null);
            setError("");
            setFileName("");
            setIdentifiersText("");
            setDelegated(false);
        }
    }, [isOpen, group.id, organizationId, remove]);

    const downloadTemplate = () => {
        const workbook = XLSX.utils.book_new();
        const sheet = XLSX.utils.aoa_to_sheet([["SourceID", "Email", "UserID"]]);
        sheet["!cols"] = [{ wch: 25 }, { wch: 40 }, { wch: 30 }];
        // Preformat identifier cells as text so Excel keeps student-number leading zeros.
        for (let row = 1; row <= 5000; row++) {
            for (let column = 0; column < 3; column++) sheet[XLSX.utils.encode_cell({ r: row, c: column })] = { t: "s", v: "", z: "@" };
        }
        sheet["!ref"] = "A1:C5001";
        XLSX.utils.book_append_sheet(workbook, sheet, "Members");
        XLSX.writeFile(workbook, "Group Members Import Template.xlsx");
    };

    const readFile = async (file?: File) => {
        setPreview(null);
        setMembers([]);
        setError("");
        setFileName(file?.name || "");
        setIdentifiersText("");
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".xlsx") || file.size > 10 * 1024 * 1024) {
            setError("Choose an Excel file (.xlsx) no larger than 10MB.");
            return;
        }
        setBusy(true);
        try {
            const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
            const rows = XLSX.utils.sheet_to_json<string[]>(workbook.Sheets[workbook.SheetNames[0]], { header: 1, defval: "", raw: false });
            const headings = (rows[0] || []).map(value => String(value).trim().toLowerCase().replace(/[\s_-]/g, ""));
            const emailColumn = headings.indexOf("email");
            const idColumn = headings.indexOf("userid");
            const sourceColumn = headings.findIndex(value => ["sourceid", "studentid", "studentnumber"].includes(value));
            if (emailColumn < 0 && idColumn < 0 && sourceColumn < 0) throw new Error("The first row must include a SourceID, Email or UserID column. Use the template below.");
            const identifiers = rows.slice(1).filter(row => row.some(value => String(value).trim())).map((row, index) => {
                const identifier = String(row[idColumn] || "").trim() || String(row[emailColumn] || "").trim() || String(row[sourceColumn] || "").trim();
                if (!identifier) throw new Error(`Member entry ${index + 1} needs a SourceID, Email or UserID.`);
                return identifier;
            });
            const uniqueMembers = Array.from(new Set(identifiers));
            if (uniqueMembers.length === 0 || uniqueMembers.length > 5000) throw new Error("Include between 1 and 5,000 members in the first worksheet.");
            setMembers(uniqueMembers);
        } catch (err) {
            setError(err.message || "Could not read the Excel file. Use the template below.");
        } finally {
            setBusy(false);
        }
    };

    const submit = async (previewOnly: boolean) => {
        setBusy(true);
        setError("");
        try {
            const result = await ImportGroupMembers(organizationId, group.id, members, previewOnly, delegated, remove);
            setPreview(result);
            if (!result.success) setError(result.message || `Could not ${action.toLowerCase()} members.`);
            else if (!previewOnly) onImported(remove ? result.removedCount : result.addedCount);
        } catch (err) {
            setPreview(null);
            setError(err.response?.data?.message || `Could not ${action.toLowerCase()} members. Please try again.`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={() => !busy && onClose()} title={`${action} Members ${remove ? "from" : "into"} ${group.name}`} icon={["fad", remove ? "trash" : "upload"]} size="lg" closeOnOverlayClick={!busy}>
            <div className="p-6 space-y-4">
                <p className="text-sm text-gray-700">
                    Find users by SourceID (student number), email address or user ID in this organization.
                    {remove ? " Only direct memberships will be removed, including owners. Child-group memberships and user accounts will be kept." : " New entries are added as members; existing members and owners keep their roles. Only active users can be imported."}
                </p>
                <label className="block text-sm font-medium text-gray-700">
                    Student numbers, emails or user IDs (one per line)
                    <textarea disabled={busy} rows={3} value={identifiersText} placeholder="Enter a student number to find the student and their linked parents"
                        className="block mt-2 w-full border border-gray-300 rounded-md p-2"
                        onChange={e => {
                            setIdentifiersText(e.target.value);
                            setFileName("");
                            setPreview(null);
                            setError("");
                            setMembers(Array.from(new Set(e.target.value.split(/\r?\n/).map(value => value.trim()).filter(Boolean))));
                        }} />
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" role="switch" checked={delegated} disabled={busy} onChange={e => { setDelegated(e.target.checked); setPreview(null); setError(""); }} />
                    Include delegated users (linked parents)
                </label>
                <p className="text-xs text-gray-600">When enabled, the matched student and users they have given access to are included. Review every resolved user before confirming.</p>
                <label className="block text-sm font-medium text-gray-700">
                    Or upload a members file (.xlsx, max 10MB, 5,000 entries)
                    <input type="file" accept=".xlsx" disabled={busy} onChange={e => { readFile(e.target.files?.[0]); e.target.value = ""; }} className="block mt-2 w-full text-sm" />
                </label>
                <p className="text-xs text-gray-600">Use SourceID, Email or UserID columns in the first worksheet. If multiple columns are filled, UserID takes priority, then Email, then SourceID. Format student numbers as text to preserve leading zeros.</p>
                {fileName && members.length > 0 && <p className="text-sm text-gray-600">{fileName}: {members.length} entries loaded.</p>}
                {members.length > 5000 && <p role="alert" className="text-sm text-red-700">Use at most 5,000 identifiers per operation.</p>}
                {preview && (
                    <>
                        <p className="text-sm text-gray-700">{preview.members.filter(m => m.status === (remove ? "Ready to remove" : "Ready to add")).length} members to {remove ? "remove" : "add"}. Duplicate users and {remove ? "users without a direct membership" : "existing memberships"} will be skipped.</p>
                        <div className="max-h-80 overflow-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead><tr><th className="p-2 text-left">Identifier</th><th className="p-2 text-left">Name / User ID</th><th className="p-2 text-left">Status</th></tr></thead>
                                <tbody>{preview.members.map(member => (
                                    <tr key={`${member.identifier}:${member.userID}`}><td className="p-2">{member.identifier}</td><td className="p-2">{member.name || member.userID || "—"}{member.isDelegated && <span className="block text-xs text-blue-700">Delegated user (linked parent)</span>}<span className="block text-xs text-gray-500">{member.userID}</span></td><td className={`p-2 ${member.userID ? "text-gray-700" : "text-red-700"}`}>{member.status}</td></tr>
                                ))}</tbody>
                            </table>
                        </div>
                    </>
                )}
                {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            </div>
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex flex-wrap justify-between gap-3">
                <Button variant="outline-primary" onClick={downloadTemplate} disabled={busy}>Download Template</Button>
                <div className="flex gap-3">
                    <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                    {preview?.success ? (
                        <Button variant={remove ? "danger" : "primary"} onClick={() => submit(false)} disabled={busy}>{busy ? `${remove ? "Removing" : "Importing"}...` : `Confirm ${remove ? "Removal" : "Import"}`}</Button>
                    ) : (
                        <Button variant="primary" onClick={() => submit(true)} disabled={busy || members.length === 0 || members.length > 5000}>{busy ? "Loading..." : "Find / Preview Members"}</Button>
                    )}
                </div>
            </div>
        </Modal>
    );
};
